import { type FC, type ReactNode } from "react";
import { Box, Grid, alpha, styled } from "@mui/material";
import type { DeviceMode } from "../../model/types";
import { byDevice } from "./byDevice";
import { getCoverOverlay, type CoverAlign } from "./getCoverOverlay";
import { BackgroundImage } from "./BackgroundImage";
import { BackgroundVideo } from "./BackgroundVideo";
import { CoverText } from "./CoverText";
import { CoverButtons, type CoverButtonItem } from "./CoverButtons";
import type { CoverTextVariant } from "./coverTextStyles";

export type CoverVerticalAlign = "top" | "center" | "bottom";

export interface CoverViewProps {
  device: DeviceMode;
  align: CoverAlign;
  verticalAlign: CoverVerticalAlign;
  title?: {
    text: string;
    variant: CoverTextVariant;
    color: "textPrimary" | "textSecondary";
    weight?: string;
  };
  subTitle?: {
    text: string;
    variant: CoverTextVariant;
    color: "textPrimary" | "textSecondary";
    weight?: string;
  };
  background?: {
    desktopImageId?: string | null;
    mobileImageId?: string | null;
    desktopVideoId?: string | null;
    mobileVideoId?: string | null;
    brightness?: number | null;
  };
  buttons?: CoverButtonItem[];
  buttonsColor?: "primary" | "secondary";
  buttonsSize?: "small" | "medium" | "large";
  /** Подсказка поверх фона, когда фон ещё не выбран. */
  backgroundHint?: ReactNode;
  onSelectBackground?: () => void;
  onSelectTitle?: () => void;
  onSelectSubTitle?: () => void;
  onSelectButtons?: () => void;
}

const horizontalAlignToFlex: Record<string, string> = {
  left: "flex-start",
  center: "center",
  right: "flex-end",
};

const verticalAlignToFlex: Record<string, string> = {
  top: "flex-start",
  center: "center",
  bottom: "flex-end",
};

// Копия корня витрины (CoverBlock.tsx). Отличия: высоту задаёт рамка
// устройства (height 100%, а не 100vh), нет first/last с их отрицательными
// отступами, ветвление по device вместо брейкпоинтов.
const Root = styled(Box, {
  shouldForwardProp: (prop) =>
    !["coverAlign", "coverVerticalAlign", "device"].includes(prop as string),
})<{
  coverAlign: CoverAlign;
  coverVerticalAlign: CoverVerticalAlign;
  device: DeviceMode;
}>(({ theme, coverAlign, coverVerticalAlign, device }) => ({
  overflow: "hidden",
  position: "relative",
  // zIndex 0 обязателен, в отличие от витрины. Там фон рисует body, а фон
  // body браузер кладёт на канву, поэтому слои обложки с zIndex -1 видно.
  // Здесь фон рисует ScopedCssBaseline обычным div, и без своего контекста
  // наложения картинка с zIndex -1 уехала бы за этот фон.
  zIndex: 0,
  height: "100%",
  width: "100%",
  boxSizing: "border-box",
  display: "flex",
  padding: byDevice(device, {
    mobile: "80px 16px 120px",
    desktop: "100px 166px 160px",
  }),
  alignItems: verticalAlignToFlex[coverVerticalAlign] || "center",
  justifyContent: horizontalAlignToFlex[coverAlign] || "center",

  // Gradient overlay внизу.
  "&:before": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: "30%",
    pointerEvents: "none",
    zIndex: 0,
    backgroundImage: `linear-gradient(transparent, ${alpha(
      theme.palette.background.default,
      0.1
    )}, ${alpha(theme.palette.background.default, 0.5)}, ${
      theme.palette.background.default
    })`,
  },

  // Затемнение со стороны текста (макет 1a).
  "&:after": {
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    zIndex: 0,
    backgroundImage: getCoverOverlay(
      coverAlign,
      theme.palette.background.default
    ),
  },
}));

const HitLayer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "device",
})<{ device: DeviceMode }>(({ device }) => ({
  position: "absolute",
  inset: 0,
  zIndex: 1,
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  paddingTop: byDevice(device, { mobile: "20%", desktop: "10%" }),
  cursor: "pointer",
}));

// Блок текста нарисован поверх HitLayer, поэтому его пустая площадь глотала
// бы клики со стандартным курсором. Отключаем у него события мыши: клик
// проваливается на HitLayer и открывает выбор фона, а сами тексты и кнопки
// возвращают себе pointerEvents: auto.
const Inner = styled(Box)({
  maxWidth: 640,
  width: "100%",
  position: "relative",
  zIndex: 1,
  pointerEvents: "none",
});

export const CoverView: FC<CoverViewProps> = ({
  device,
  align,
  verticalAlign,
  title,
  subTitle,
  background,
  buttons = [],
  buttonsColor = "primary",
  buttonsSize = "medium",
  backgroundHint,
  onSelectBackground,
  onSelectTitle,
  onSelectSubTitle,
  onSelectButtons,
}) => {
  return (
    <Root
      data-testid="CoverView"
      coverAlign={align}
      coverVerticalAlign={verticalAlign}
      device={device}
    >
      {background && (
        <>
          <BackgroundImage
            desktopImageId={background.desktopImageId}
            mobileImageId={background.mobileImageId}
            device={device}
            align={align}
            verticalAlign={verticalAlign}
            brightness={background.brightness}
          />
          {/* После картинки: тот же zIndex -1, но выше по DOM, поэтому
              картинка остаётся фолбэком, пока видео не готово. */}
          <BackgroundVideo
            desktopVideoId={background.desktopVideoId}
            mobileVideoId={background.mobileVideoId}
            device={device}
            brightness={background.brightness}
          />
        </>
      )}

      {/* Слой кликов по фону идёт до Inner, поэтому тексты и кнопки
          перехватывают клик первыми. Всё остальное, включая пустую площадь
          блока текста, проваливается сюда и открывает выбор фона. */}
      <HitLayer device={device} onClick={onSelectBackground}>
        {backgroundHint}
      </HitLayer>

      <Inner data-testid="CoverInner">
        <Grid
          container
          spacing={3}
          alignItems="center"
          sx={{ justifyContent: horizontalAlignToFlex[align] || "center" }}
        >
          <Grid size={12}>
            {title && title.text && (
              <CoverText
                text={title.text}
                variant={title.variant}
                color={title.color}
                weight={title.weight}
                align={align}
                device={device}
                gutterBottom
                onClick={onSelectTitle}
              />
            )}
            {subTitle && subTitle.text && (
              <CoverText
                text={subTitle.text}
                variant={subTitle.variant}
                color={subTitle.color}
                weight={subTitle.weight}
                align={align}
                device={device}
                onClick={onSelectSubTitle}
              />
            )}
          </Grid>

          {buttons.length > 0 && (
            <Grid size={12}>
              <CoverButtons
                buttons={buttons}
                color={buttonsColor}
                size={buttonsSize}
                align={align}
                device={device}
                onClick={onSelectButtons}
              />
            </Grid>
          )}
        </Grid>
      </Inner>
    </Root>
  );
};
